import { Box, Button, Card, CardBody, Flex, Text as CText, Tooltip } from '@chakra-ui/react'
import type { AssetId } from '@shapeshiftoss/caip'
import type { JSX, PropsWithChildren } from 'react'
import { useCallback } from 'react'

import { WatchAssetButton } from '@/components/AssetHeader/WatchAssetButton'
import { AssetIcon } from '@/components/AssetIcon'
import { RawText } from '@/components/Text'
import { vibrate } from '@/lib/vibrate'

type CommonStatProps = {
  label: string | JSX.Element
  value: string | JSX.Element
  align?: 'flex-start' | 'center' | 'flex-end'
}

export const CommonStat: React.FC<CommonStatProps> = ({ label, value, align = 'flex-start' }) => {
  return (
    <Flex direction='column' gap={1} justifyContent={align} alignItems={align}>
      <Box fontSize='lg'>
        {typeof value === 'string' ? <RawText>{value}</RawText> : <>{value}</>}
      </Box>
      <Box fontSize='sm' fontWeight='normal'>
        {typeof label === 'string' ? <RawText color='text.subtle'>{label}</RawText> : <>{label}</>}
      </Box>
    </Flex>
  )
}

type CommonCardProps = {
  title: string
  subtitle: string
  assetId: AssetId
  onClick: (assetId: AssetId) => void
} & PropsWithChildren

export const CommonCard: React.FC<CommonCardProps> = ({
  title,
  subtitle,
  assetId,
  onClick,
  children,
}) => {
  const handleClick = useCallback(() => {
    vibrate('heavy')
    onClick(assetId)
  }, [assetId, onClick])

  if (!assetId) return null

  return (
    <Card
      as={Button}
      aspectRatio='4/3'
      height='100%'
      width='100%'
      borderRadius='2xl'
      p={0}
      onClick={handleClick}
    >
      <CardBody
        as={Flex}
        flexDirection='column'
        justifyContent='space-between'
        py={6}
        px={6}
        width='100%'
        height='100%'
      >
        <Flex align='center'>
          <AssetIcon assetId={assetId} size='md' mr={2} />
          <Box textAlign='left' overflow='hidden' width='100%'>
            <Tooltip label={title} placement='top-start'>
              <CText fontWeight='medium' textOverflow='ellipsis' overflow='hidden'>
                {title}
              </CText>
            </Tooltip>
            <CText
              fontSize='sm'
              color='text.subtle'
              textOverflow='ellipsis'
              overflow='hidden'
              fontWeight='normal'
              mt={1}
            >
              {subtitle}
            </CText>
          </Box>
          <WatchAssetButton assetId={assetId} alignSelf='flex-start' />
        </Flex>
        {children && (
          <Flex mt={4} justify='space-between'>
            {children}
          </Flex>
        )}
      </CardBody>
    </Card>
  )
}
